import {
  Box,
  Divider,
  Flex,
  MobileDataTableCardSurface,
  Text,
  type MobileCardRendererProps,
  type Transfer,
} from '@talos/kyoko';
import styled from 'styled-components';

/**
 * Mobile card for a single Transfer. Designed to be passed to `MobileDataTable`
 * via the `cardRenderer` prop.
 *
 * Layout mirrors MobileOrderCard:
 *  - Row 1: provider/market (left), status badge (right)
 *  - Row 2: submit time (left), transfer ID (right)
 *  - Divider
 *  - Row 3: amount label (left), amount + currency (right)
 *  - Row 4: source label (left), source account (right)
 *  - Row 5: destination label (left), destination account (right)
 */
export function MobileTransferCard({ renderCell, onPress }: MobileCardRendererProps<Transfer>) {
  return (
    <MobileDataTableCardSurface type="button" data-testid="mobile-transfer-card" onClick={onPress}>
      <Flex justifyContent="space-between" alignItems="center" gap="spacing8">
        <CardValue
          flex="1 1 auto"
          overflow="hidden"
          textOverflow="clip"
          whiteSpace="nowrap"
          data-testid="mobile-transfer-card-provider"
        >
          {renderCell('Market')}
        </CardValue>
        <CardValue data-testid="mobile-transfer-card-status" flex="0 0 auto">
          {renderCell('Status')}
        </CardValue>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap="spacing8">
        <CardValue data-testid="mobile-transfer-card-submit-time">{renderCell('SubmitTime')}</CardValue>
        <Flex alignItems="center" gap="spacing4" overflow="hidden">
          <Text data-testid="mobile-transfer-card-id-label" fontSize="fontSizeLg">
            ID
          </Text>
          <CardValue
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            data-testid="mobile-transfer-card-id"
          >
            {renderCell('TransferID', { overrideColumnVisibility: true })}
          </CardValue>
        </Flex>
      </Flex>

      <Divider color="backgroundDivider" />

      <Flex justifyContent="space-between" alignItems="center" gap="spacing8">
        <Text data-testid="mobile-transfer-card-amount-label" color="colorTextSubtle" fontSize="fontSizeLg">
          Amount
        </Text>
        <AmountValue
          overflow="hidden"
          textOverflow="clip"
          whiteSpace="nowrap"
          data-testid="mobile-transfer-card-amount"
        >
          {renderCell('Amount')}
        </AmountValue>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap="spacing8">
        <Text data-testid="mobile-transfer-card-source-label" color="colorTextSubtle" fontSize="fontSizeLg">
          Source
        </Text>
        <CardValue
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          data-testid="mobile-transfer-card-source-account"
        >
          {renderCell('sourceAccount')}
        </CardValue>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap="spacing8">
        <Text data-testid="mobile-transfer-card-destination-label" color="colorTextSubtle" fontSize="fontSizeLg">
          Destination
        </Text>
        <CardValue
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          data-testid="mobile-transfer-card-destination-account"
        >
          {renderCell('destinationAccount')}
        </CardValue>
      </Flex>
    </MobileDataTableCardSurface>
  );
}

const CardValue = styled(Box)`
  color: ${({ theme }) => theme.colors.gray['100']};
`;

const AmountValue = styled(Box)`
  [data-testid='rounded-number'],
  [data-testid='full-number'] {
    color: ${({ theme }) => theme.colors.gray['100']};
  }
`;
